import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';

const COLLAPSED_MAX_HEIGHT = 90;

type EventTimelineEntryNoteProps = {
  note: ReactNode;
};

const EventTimelineEntryNote = ({ note }: EventTimelineEntryNoteProps) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    if (isExpanded) return;
    const element = contentRef.current;
    setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
  }, [note, isExpanded]);

  return (
    <Box
      data-testid="EventTimelineEntryNote"
      bgcolor="action.hover"
      border={1}
      borderColor="divider"
      borderRadius={1}
      p={1}
    >
      <Box
        ref={contentRef}
        data-testid="EventTimelineEntryNote.Content"
        sx={{
          maxHeight: isExpanded ? 'none' : COLLAPSED_MAX_HEIGHT,
          overflow: isExpanded ? 'visible' : 'hidden',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {note}
      </Box>
      {isOverflowing && (
        <Button
          data-testid="EventTimelineEntryNote.ToggleButton"
          size="small"
          variant="text"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(expanded => !expanded)}
          startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mt: 1 }}
        >
          {isExpanded
            ? t('EventTimeline.showLess')
            : t('EventTimeline.showMore')}
        </Button>
      )}
    </Box>
  );
};

export default EventTimelineEntryNote;
